import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Video, Calendar, Clock, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const JOIN_WINDOW_MS = 15 * 60 * 1000; // 15 minutes before scheduled start

async function fetchMeetings(role, token) {
  const endpoint = role === 'parent'
    ? `${BACKEND_URL}/api/parent/upcoming-meetings`
    : `${BACKEND_URL}/api/observer/upcoming-meetings`;
  try {
    const res = await axios.get(endpoint, { params: { token } });
    return res.data.meetings || [];
  } catch (_e) {
    return [];
  }
}

/**
 * Reusable widget that lists upcoming meetings for the current role and
 * enables a prominent "Join meeting" button only when the scheduled time is
 * within ±15 minutes (also enabled while meeting.status === 'live').
 */
export default function UpcomingMeetingsWidget({ role, token }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    fetchMeetings(role, token).then((list) => {
      setMeetings(list);
      setLoading(false);
    });
  }, [role, token]);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(tick);
  }, []);

  if (loading) return null;
  if (meetings.length === 0) return null;

  return (
    <Card className="border-0 shadow-md" data-testid={`upcoming-meetings-${role}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Video className="w-5 h-5 text-orange-600" />
          Upcoming meetings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {meetings.map((m) => {
          const start = new Date(m.scheduled_at).getTime();
          const diff = start - now;
          const isLive = m.status === 'live';
          const canJoin = isLive || (diff <= JOIN_WINDOW_MS && diff > -m.duration_minutes * 60 * 1000);
          const startLabel = new Date(m.scheduled_at).toLocaleString(undefined, {
            weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
          });
          return (
            <div
              key={m.id}
              className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                isLive ? 'border-red-300 bg-red-50/50' : 'border-orange-100 bg-orange-50/30'
              }`}
              data-testid={`meeting-${m.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gray-900 truncate">{m.title}</p>
                  {isLive && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-700 animate-pulse">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
                      Live now
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-600">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" /> {startLabel}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> {m.duration_minutes} min
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" /> {m.principal_name}
                  </span>
                </div>
                {m.agenda && (
                  <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{m.agenda}</p>
                )}
              </div>
              <Button
                onClick={() => window.open(m.meeting_link, '_blank', 'noopener,noreferrer')}
                disabled={!canJoin}
                className={canJoin
                  ? 'bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white whitespace-nowrap'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed whitespace-nowrap'}
                data-testid={`join-meeting-${m.id}`}
              >
                <Video className="w-4 h-4 mr-1.5" />
                {isLive ? 'Join now' : canJoin ? 'Join meeting' : `Opens 15 min before`}
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
